import { useMemo, useState } from "react";

import clsx from "clsx";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { type TopLevelBreakdown } from "@features/sales-v3/types";
import { type FloatingPosition } from "@mantine/core";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { Tooltip } from "../tooltip";

interface Props {
  breakdown: TopLevelBreakdown;
  tooltipPlacement?: FloatingPosition;
  onDelete(): void;
}

export const FilterButtonActive = ({
  breakdown,
  tooltipPlacement,
  onDelete,
}: Props) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const [aboutToDelete, setAboutToDelete] = useState(false);

  const breakdownConfig = useMemo(
    () =>
      breakdownConfigs.find((e) => e.key === breakdown.key) as
        | CategoricalBreakdownConfig
        | undefined,
    [breakdownConfigs, breakdown],
  );

  const breakdownConfigValues = useMemo(
    () =>
      breakdownConfig
        ? breakdownConfig.choices
            .filter((e) => breakdown.values.includes(e.value))
            .map((e) => e.label)
        : [],
    [breakdownConfig, breakdown],
  );

  if (!breakdownConfig) {
    return null;
  }

  return (
    <Tooltip
      placement={tooltipPlacement}
      filter={{
        breakdown: breakdownConfig.name,
        total: breakdownConfig.choices.length,
        values: breakdownConfigValues,
      }}
    >
      <div
        className={clsx(
          "border rounded-2xl flex items-center",
          "h-8 pl-4 pr-2 min-w-[120px] max-w-[200px]",
          {
            "border-cosmic bg-cosmic-3": !aboutToDelete,
            "border-mars-2 bg-mars-2": aboutToDelete,
          },
        )}
      >
        {aboutToDelete ? (
          <div className="flex">
            <div className="text-sm mr-4">Delete filter?</div>
            <button
              className="font-bold text-[#F37F5B] px-1 py-0.5 rounded hover:bg-white mr-1"
              onClick={onDelete}
            >
              YES
            </button>
            <button
              className="font-bold text-primary px-1 py-0.5 rounded hover:bg-white mr-1"
              onClick={() => setAboutToDelete(false)}
            >
              NO
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-hidden mr-2">
              <div className="text-xxxs font-bold mb-0.5 truncate">
                <span className="uppercase">{breakdownConfig.name}</span> (
                {breakdown.values.length} of {breakdownConfig.choices.length})
              </div>
              <div className="text-xs leading-3 truncate">
                {breakdownConfigValues.join(", ")}
              </div>
            </div>
            <div className="shrink-0">
              <button
                className={clsx(
                  "flex items-center justify-center text-xs hover:text-cosmic+1",
                  "hover:bg-white rounded-full w-4 h-4",
                )}
                onClick={() => setAboutToDelete(true)}
              >
                <i className="fa-regular fa-sharp fa-xmark mt-0.5" />
              </button>
            </div>
          </>
        )}
      </div>
    </Tooltip>
  );
};
